import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE, getRouteMeta } from '@/lib/seo';

/**
 * Route-driven SEO. Mounted once in AppShell. React 19 hoists the
 * <title>/<meta>/<link> tags below into <head>, replacing prior values
 * on each client-side navigation — so no react-helmet is needed.
 */
export default function Seo() {
  const { pathname } = useLocation();
  const meta = getRouteMeta(pathname);

  // The static SEO tags in index.html (home defaults, kept for no-JS social
  // scrapers) would otherwise coexist with the React-managed ones below,
  // producing duplicate description/canonical/og tags. Remove them once JS
  // takes over; React's tags then become the single source of truth.
  useEffect(() => {
    document
      .querySelectorAll('[data-seo-default]')
      .forEach((el) => el.remove());
  }, []);

  const isHome = pathname === '/';
  const fullTitle = isHome ? meta.title : `${meta.title} — ${SITE.titleSuffix}`;
  const canonical = SITE.url + (pathname === '/' ? '/' : pathname);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={canonical} />
      {meta.noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={SITE.ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content={SITE.twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={SITE.ogImage} />
    </>
  );
}
